import {
  DEAL_QUEUE_SORTS,
  DEAL_QUEUE_TABS,
  type DealQueueSort,
  type DealQueueTab,
} from '@carsfinders/contracts';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useState } from 'react';

import { AccessDenied } from '../../components/access-denied';
import { ApiError } from '../../lib/api';
import { t } from '../../lib/i18n';
import { pluralUk } from '../finance/utils';
import { DealsBoard } from './components/deals-board';
import {
  DEALS_SEARCH_DEBOUNCE_MS,
  useDealsQueue,
  useDebouncedValue,
} from './hooks/use-deals-queue';

/**
 * The order the screen opens with — the client «Мої ставки» default (owner,
 * 2026-10-12: «додай сортування так як у клієнта»). The server's own default
 * stays the work queue, so an older admin build keeps its order.
 */
export const DEFAULT_DEALS_SORT: DealQueueSort = 'endSoon';

/** `/deals?sort=` — anything unknown falls back to «Закінчуються скоро». */
export function parseDealsSort(value: unknown): DealQueueSort {
  return typeof value === 'string' && (DEAL_QUEUE_SORTS as readonly string[]).includes(value)
    ? (value as DealQueueSort)
    : DEFAULT_DEALS_SORT;
}

/** `/deals?client=` — a `users._id`; anything else means «Усі клієнти». */
export function parseDealsClient(value: unknown): string {
  return typeof value === 'string' && /^[a-f0-9]{24}$/i.test(value) ? value : '';
}

/** `/deals?auction=` — an auction house name (scc, Rest, AXA…); anything else means «Усі аукціони». */
export function parseDealsAuction(value: unknown): string {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= 80
    ? value.trim()
    : '';
}

/** `/deals?tab=` — anything that is not a known tab falls back to «Активні». */
export function parseDealsTab(value: unknown): DealQueueTab {
  return typeof value === 'string' && (DEAL_QUEUE_TABS as readonly string[]).includes(value)
    ? (value as DealQueueTab)
    : 'active';
}

/** «12 угод» — the form is computed; the artboard's mock count happened to fit one. */
function dealCount(n: number): string {
  const forms = {
    one: t('admin.deals.count.one', { n }),
    few: t('admin.deals.count.few', { n }),
    many: t('admin.deals.count.many', { n }),
  };
  return pluralUk(n, forms);
}

/**
 * «Угоди» — `Redesign-D-Admin`, artboard «Admin · Угоди». ONE queue for what
 * used to be two screens («Ставки» and «Виграні авто»): every client lot is one
 * row, from the relay to the handover, and each row offers only its next action.
 *
 * The rows, their stage and that action come from `GET /api/admin/deals/queue`
 * (phase 7), which resolves them with the contract's `resolveDealStage` — this
 * screen renders, it never re-derives a stage. Counters are server-side and
 * honour the search, so every tab badge is right at once. The board itself is
 * `DealsBoard`, shared with the client page's «Ставки» tab.
 *
 * The tab and the order live in the URL (`?tab=relay&sort=bidHigh`) because
 * the retired routes redirect INTO a tab: `/bids/relay` → «Ретрансляція»,
 * `/won-cars` → «Доставка».
 */
export function DealsPage() {
  const search = useSearch({ strict: false }) as {
    tab?: unknown;
    sort?: unknown;
    client?: unknown;
    auction?: unknown;
  };
  const navigate = useNavigate();
  const tab = parseDealsTab(search.tab);
  const sort = parseDealsSort(search.sort);
  const client = parseDealsClient(search.client);
  const auction = parseDealsAuction(search.auction);

  const [query, setQuery] = useState('');
  const q = useDebouncedValue(query.trim(), DEALS_SEARCH_DEBOUNCE_MS);
  // Same key as the board's own read — react-query serves both from one request.
  const queue = useDealsQueue(tab, q, sort, client || undefined, auction || undefined);

  /** The URL carries only what differs from the defaults («Активні», «Закінчуються скоро»). */
  const go = (
    nextTab: DealQueueTab,
    nextSort: DealQueueSort,
    nextClient: string = client,
    nextAuction: string = auction,
  ) => {
    void navigate({
      to: '/deals',
      search: {
        ...(nextTab === 'active' ? {} : { tab: nextTab }),
        ...(nextSort === DEFAULT_DEALS_SORT ? {} : { sort: nextSort }),
        ...(nextClient ? { client: nextClient } : {}),
        ...(nextAuction ? { auction: nextAuction } : {}),
      },
      replace: true,
    });
  };

  const error = queue.error;
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    return <AccessDenied />;
  }

  const counts = queue.counts;
  const eyebrow = counts
    ? t('admin.deals.eyebrow', {
        total: dealCount(counts.all ?? 0),
        relay: String(counts.relay ?? 0),
        invoice: String(counts.won_uninvoiced ?? 0),
        payment: String(counts.payment ?? 0),
      })
    : t('admin.deals.loading');

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[26px] leading-[1.1] font-semibold tracking-[-0.02em] lg:text-[30px]">
            {t('admin.deals.title')}
          </h1>
          <p className="mt-1.5 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase lg:mt-2 lg:text-[11px] lg:tracking-[0.14em]">
            {eyebrow}
          </p>
        </div>
        <label className="flex h-10 w-[320px] max-w-full items-center gap-2.5 rounded-cf-md border border-cf-line bg-cf-surface px-3.5 focus-within:border-cf-ink">
          <Search
            size={16}
            strokeWidth={1.75}
            className="shrink-0 text-cf-ink-subtle"
            aria-hidden
          />
          <span className="sr-only">{t('admin.deals.searchLabel')}</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('admin.deals.searchPlaceholder')}
            className="min-w-0 flex-1 border-0 bg-transparent text-sm text-cf-ink outline-none placeholder:text-cf-ink-subtle"
          />
        </label>
      </div>

      <DealsBoard
        tab={tab}
        sort={sort}
        onTabChange={(next) => go(next, sort)}
        onSortChange={(next) => go(tab, next)}
        q={q}
        userId={client || undefined}
        onClientChange={(next) => go(tab, sort, next)}
        auction={auction || undefined}
        onAuctionChange={(next) => go(tab, sort, client, next)}
      />

      <p className="text-xs leading-[1.5] text-cf-ink-subtle">{t('admin.deals.footnote')}</p>
    </>
  );
}
